import { Button, Chip, TableCell, TableRow } from '@heroui/react';
import React from 'react';
import { FiPercent } from 'react-icons/fi';

import { BaseModal } from '../../../../../../../components/BaseModal';
import {
  LoadingTable,
  type LoadingTableColumn,
} from '../../../../../../../components/Table';
import { type TaxJurisdictionRow, type TaxRateRow } from '../types';
import {
  ABSENT_VALUE,
  formatTaxRateDate,
  formatTaxRateValue,
  taxBehaviorLabel,
  taxRateCategoryLabel,
} from '../utils/taxJurisdictionMapper';

interface TaxJurisdictionDetailModalProps {
  /** The selected jurisdiction row, or null when the modal is closed. */
  jurisdiction: TaxJurisdictionRow | null;
  onClose: () => void;
}

const columns: LoadingTableColumn[] = [
  { label: 'Code', className: 'pl-6 min-w-24' },
  { label: 'Category', className: 'min-w-28' },
  { label: 'Label', className: 'min-w-32' },
  { label: 'Value', className: 'min-w-24' },
  { label: 'Behavior', className: 'min-w-40' },
  { label: 'Applies to free products', className: 'min-w-28' },
  { label: 'Priority', className: 'min-w-20' },
  { label: 'Valid from', className: 'min-w-28' },
  { label: 'Valid to', className: 'min-w-28' },
];

export const TaxJurisdictionDetailModal: React.FC<
  TaxJurisdictionDetailModalProps
> = ({ jurisdiction, onClose }) => {
  const rates = jurisdiction?.rates ?? [];

  const renderRow = (rate: TaxRateRow) => (
    <TableRow key={rate.id}>
      <TableCell className="pl-6">
        <span className="font-mono text-xs text-default-600">
          {rate.code ?? ABSENT_VALUE}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-foreground">
          {taxRateCategoryLabel(rate.category)}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">
          {rate.label ?? ABSENT_VALUE}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm font-medium text-foreground">
          {formatTaxRateValue(rate.value)}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">
          {taxBehaviorLabel(rate.taxBehavior)}
        </span>
      </TableCell>
      <TableCell>
        <Chip
          size="sm"
          variant="flat"
          color={rate.appliesToFreeProducts ? 'success' : 'default'}
        >
          {rate.appliesToFreeProducts ? 'Yes' : 'No'}
        </Chip>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">{rate.priority}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">
          {formatTaxRateDate(rate.validFrom)}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">
          {formatTaxRateDate(rate.validTo)}
        </span>
      </TableCell>
    </TableRow>
  );

  const subtitle = jurisdiction
    ? [jurisdiction.countryCode, jurisdiction.subdivisionCode]
        .filter(Boolean)
        .join(' / ')
    : undefined;

  return (
    <BaseModal
      isOpen={jurisdiction !== null}
      onClose={onClose}
      size="5xl"
      title={jurisdiction ? `Rates for ${jurisdiction.code}` : 'Rates'}
      subtitle={subtitle}
      scrollBehavior="inside"
      footer={
        <div className="flex w-full items-center justify-between">
          <span className="text-xs text-default-400">
            Read-only. Tax rates are configured by the distributor.
          </span>
          <Button variant="flat" onPress={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <LoadingTable
          columns={columns}
          data={rates}
          renderRow={renderRow}
          isLoading={false}
          isEmpty={rates.length === 0}
          ariaLabel="Tax rates table"
          emptyState={{
            title: 'No rates',
            description: 'This jurisdiction has no tax rates configured.',
            icon: FiPercent,
          }}
        />
      </div>
    </BaseModal>
  );
};
